import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface PricingCardProps {
  name: string;
  price: number;
  carrierLimit: string;
  features: string[];
  isPopular: boolean;
  isRecommended?: boolean;
}

const PricingCard: React.FC<PricingCardProps> = ({
  name,
  price,
  carrierLimit,
  features,
  isPopular,
  isRecommended
}) => {
  const { isAuthenticated, profile, loading: authLoading } = useAuth();
const hasHadTrial =
    (profile as any)?.trial_started_at !== null ||
    (profile as any)?.trial_ends_at !== null;

  const planSlug = name.toLowerCase().replace(' ', '-');

  return (
    <div
      className={`bg-white rounded-lg shadow-lg overflow-hidden ${
        isRecommended
          ? 'border-4 border-green-500 ring-4 ring-green-200'
          : isPopular
          ? 'border-2 border-blue-500'
          : ''
      }`}
    >
      {isRecommended && (
        <div className="bg-green-500 text-white text-center py-2 font-medium">
          ✨ Recommended For You
        </div>
      )}
      {isPopular && !isRecommended && (
        <div className="bg-blue-500 text-white text-center py-2 font-medium">
          Most Popular
        </div>
      )}

      <div className="p-6">
        <h3 className="text-xl font-bold mb-2">{name}</h3>
        <div className="text-3xl font-bold mb-2">
          ${price}
          <span className="text-gray-500 text-lg font-normal">/mo</span>
        </div>
        <p className="text-sm text-gray-600 mb-6">{carrierLimit}</p>

        <ul className="space-y-3 mb-6">
          {features.map((feature, index) => (
            <li key={index} className="flex items-center">
              <CheckCircle2 className="h-5 w-5 text-green-500 mr-2" />
             <span className={index === 0 || index === features.length - 1 ? 'font-bold text-blue-700' : ''}>{feature}</span>
            </li>
          ))}
        </ul>

        {/* Two Button Options */}
        <div className="space-y-3">
          {!authLoading && (!isAuthenticated || !hasHadTrial) && (
            <a
              href={
                isAuthenticated
                  ? `/checkout?plan=${planSlug}&trial=true`
                  : `/register/broker?plan=${planSlug}&trial=true`
              }
              className={`block w-full text-center py-2 rounded-lg font-medium transition ${
                isRecommended
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : isPopular
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isAuthenticated ? 'Start Trial' : 'Start 7-Day Free Trial'}
            </a>
          )}

          <a
            href={
              isAuthenticated
                ? `/checkout?plan=${planSlug}&trial=false`
                : `/register/broker?plan=${planSlug}&trial=false`
            }
            className={`block w-full text-center py-2 rounded-lg font-medium transition ${
              hasHadTrial && isAuthenticated
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-50'
            }`}
          >
            {isAuthenticated ? 'Upgrade to This Plan' : 'Buy Now'}
          </a>
        </div>
      </div>
    </div>
  );
};

export default PricingCard;